import React from 'react';
import { createRoot } from 'react-dom/client';

import App from './App';

// Ensure reactstrap Fade transition has a default timeout to avoid
// prop-type warnings when some Alert usages omit the timeout prop.
// We avoid importing reactstrap internals directly; instead set a
// safe default on the Transition component used by reactstrap if
// available at runtime. This is defensive and will be a no-op
// if the module shape differs in other environments.
try {
  // eslint-disable-next-line global-require
  const reactstrap = require('reactstrap');
  // Instead of setting `defaultProps` on the function component (which
  // will be deprecated), wrap the original Fade component with a small
  // forwarding component that supplies a default `timeout` via a default
  // parameter. This preserves runtime behavior without relying on
  // `defaultProps`.
  if (reactstrap && reactstrap.Fade) {
    const OriginalFade = reactstrap.Fade;
    // Only replace once (defensive)
    if (!OriginalFade.__hasDefaultTimeoutWrapper) {
      const WrappedFade = ({ timeout = 100, ...rest }) => React.createElement(OriginalFade, { timeout, ...rest });
      // copy non-react static properties commonly used
      WrappedFade.propTypes = OriginalFade.propTypes;
      WrappedFade.displayName = OriginalFade.displayName || OriginalFade.name || 'Fade';
      // mark to avoid double-wrapping
      WrappedFade.__hasDefaultTimeoutWrapper = true;
      // attach original for debugging if needed
      WrappedFade.__original = OriginalFade;
      reactstrap.Fade = WrappedFade;
      // Also wrap Alert so any usage that omits `timeout` gets a sensible default.
      // Some codepaths import Alert and don't pass `timeout`, which makes the
      // Fade used internally complain about a missing required prop. We
      // provide a small wrapper that forwards all props but supplies a
      // default timeout when it's undefined.
      if (reactstrap.Alert) {
        const OriginalAlert = reactstrap.Alert;
        if (!OriginalAlert.__hasDefaultTimeoutWrapper) {
          const WrappedAlert = (props) => {
            // preserve explicit null/0 values, only default when undefined
            const alertTimeout = props.timeout === undefined ? 300 : props.timeout;
            return React.createElement(OriginalAlert, { ...props, timeout: alertTimeout });
          };
          WrappedAlert.propTypes = OriginalAlert.propTypes;
          WrappedAlert.displayName = OriginalAlert.displayName || OriginalAlert.name || 'Alert';
          WrappedAlert.__hasDefaultTimeoutWrapper = true;
          WrappedAlert.__original = OriginalAlert;
          reactstrap.Alert = WrappedAlert;
        }
      }
    }
  }
} catch (e) {
  // ignore; this is best-effort and should not break startup
}

const container = document.getElementById('pergamum');
if (container) {
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
} else {
  // Fallback for environments where the target container is missing
  // This mirrors previous behavior but avoids calling the deprecated API.
  // eslint-disable-next-line no-console
  console.error('Root container "pergamum" not found.');
}
